/**
 * Hero Floating Images – Per-card mouse-follow parallax.
 *
 * Only the hovered card moves.  Mouse position is tracked relative
 * to that card's own bounding box.  Movement is lerped per-frame
 * with requestAnimationFrame for smooth GPU-accelerated animation.
 *
 * Markup contract:
 *   <div data-hero-floating-parallax>
 *       <div class="hero-floating-image" data-parallax-depth="12">…</div>
 *   </div>
 */
export default class HeroFloatingParallax {
  constructor(container) {
    // Skip on touch-only devices — no mousemove events will fire
    if (!window.matchMedia('(hover: hover)').matches) return;

    this.container = container;
    this.cards = [...container.querySelectorAll('.hero-floating-image')];
    if (!this.cards.length) return;

    // Lerp factor — lower = smoother/laggier
    this._ease = 0.045;

    this._init();
  }

  _init() {
    this.cards.forEach((card) => {
      const depth = parseFloat(card.dataset.parallaxDepth) || 10;

      // Per-card state
      const state = {
        depth,
        targetX: 0,
        targetY: 0,
        currentX: 0,
        currentY: 0,
        targetScale: 1,
        currentScale: 1,
        rafId: null,
        hovered: false,
      };
      card._pState = state;

      card.addEventListener('mouseenter', () => {
        state.hovered = true;
        state.targetScale = 1.02;
      }, { passive: true });

      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        // Normalise cursor to –1 … +1 relative to card center
        state.targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        state.targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        if (!state.rafId) this._tick(card, state);
      }, { passive: true });

      card.addEventListener('mouseleave', () => {
        state.hovered = false;
        state.targetScale = 1;
        // Ease back to center
        state.targetX = 0;
        state.targetY = 0;
        if (!state.rafId) this._tick(card, state);
      }, { passive: true });
    });
  }

  _tick(card, state) {
    state.currentX += (state.targetX - state.currentX) * this._ease;
    state.currentY += (state.targetY - state.currentY) * this._ease;
    state.currentScale += (state.targetScale - state.currentScale) * this._ease;

    const x = state.currentX * state.depth;
    const y = state.currentY * state.depth;
    const s = state.currentScale;

    card.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s})`;

    // Keep looping until we're close enough to the target
    const dx = Math.abs(state.targetX - state.currentX);
    const dy = Math.abs(state.targetY - state.currentY);
    const ds = Math.abs(state.targetScale - state.currentScale);

    if (dx > 0.0005 || dy > 0.0005 || ds > 0.0001) {
      state.rafId = requestAnimationFrame(() => {
        state.rafId = null;
        this._tick(card, state);
      });
    } else {
      state.rafId = null;
      // Snap to exact target
      state.currentX = state.targetX;
      state.currentY = state.targetY;
      state.currentScale = state.targetScale;
      const fx = state.currentX * state.depth;
      const fy = state.currentY * state.depth;
      card.style.transform = `translate3d(${fx}px, ${fy}px, 0) scale(${state.currentScale})`;
    }
  }

  destroy() {
    this.cards.forEach((card) => {
      if (card._pState && card._pState.rafId) {
        cancelAnimationFrame(card._pState.rafId);
      }
    });
  }
}
